import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../../config";

const baseQuery = fetchBaseQuery({
  baseUrl: `${getApiBaseUrl()}/renewals`,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const user = getState().user?.currentUser;
    if (user?.id) {
      headers.set("userid", user.id);
    }
    return headers;
  },
});

export const memberRenewalApi = createApi({
  reducerPath: "memberRenewalApi",
  baseQuery,
  tagTypes: ["MemberRenewal"],
  endpoints: (builder) => ({
    getMemberRenewals: builder.query({
      async queryFn(
        {
          pageSize = 100,
          nextToken = undefined,
          page = 1,
          companyProfileId = undefined,
        } = {},
        queryApi,
        extraOptions,
        baseQuery
      ) {
        try {
          const result = await baseQuery({
            url: "",
            params: {
              pageSize,
              ...(nextToken ? { nextToken } : {}),
              page,
              ...(companyProfileId
                ? { company_profile_id: companyProfileId }
                : {}),
            },
          });
          return result;
        } catch (error) {
          console.error("Error fetching Renewals:", error);

          return {
            data: {
              data: [],
            },
          };
        }
      },
      providesTags: ["MemberRenewal"],
    }),

    // getAllMemberRenewals: builder.query({
    //   async queryFn(args, queryApi, extraOptions, baseQuery) {
    //     const result = await baseQuery({
    //       url: "all",
    //     });
    //     return result;
    //   },
    //   providesTags: ["MemberRenewal"],
    // }),

    getAllMemberRenewals: builder.query({
      query: ({ searchQuery = "", createdFrom = "", createdTo = "", page = 1, limit = 100 } = {}) => ({
        url: "",
        params: {
          ...(searchQuery && { search: searchQuery }),
          ...(createdFrom && { createdFrom }),
          ...(createdTo && { createdTo }),
          page,
          limit
        },
      }),
      providesTags: (result, error, arg) => {
        const { searchQuery = "", createdFrom = "", createdTo = "", page = 1 } = arg || {};
        return searchQuery || createdFrom || createdTo || page > 1
          ? [{ type: "MemberRenewal", id: `SEARCH-${searchQuery}-${createdFrom}-${createdTo}-${page}` }]
          : ["MemberRenewal"];
      },
    }),

    getMemberRenewalById: builder.query({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/${id}`,
        });

        return result;
      },
      providesTags: (result, error, id) => [
        { type: "MemberRenewal", id },
        "MemberRenewal",
      ],
      // query: (id) => `${id}`,
      // providesTags: ["MemberRenewal"],
    }),
    createMemberRenewal: builder.mutation({
      query: (data) => ({
        url: "",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["MemberRenewal"],
    }),
    updateMemberRenewal: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["MemberRenewal"],
    }),
    deleteMemberRenewal: builder.mutation({
      query: (id) => ({
        url: `${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["MemberRenewal"],
    }),
    // Trigger a status transition
    triggerStatusTransition: builder.mutation({
      query: ({
        renewalId,
        transitionId,
        rejection_reason,
        amendment_comments,
      }) => ({
        url: `${renewalId}/transition/${transitionId}`,
        method: "POST",
        body: {
          ...(rejection_reason && { rejection_reason }),
          ...(amendment_comments && { amendment_comments }),
        },
      }),
      invalidatesTags: (result, error, { renewalId }) => [
        { type: "MemberRenewal", id: renewalId },
        { type: "Transitions", id: renewalId },
        "MemberRenewal",
      ],
    }),

    // Revert to previous status
    revertMemberRenewalStatus: builder.mutation({
      query: ({ renewalId }) => ({
        url: `${renewalId}/revert`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { renewalId }) => [
        { type: "MemberRenewal", id: renewalId },
        { type: "Transitions", id: renewalId },
        "MemberRenewal",
      ],
    }),
    setManualRenewalStatus: builder.mutation({
      query: ({ renewalId, targetStatusId, reason = "" }) => ({
        url: `${renewalId}/manual-status`,
        method: "POST",
        body: { targetStatusId, reason },
      }),
      invalidatesTags: (result, error, { renewalId }) => [
        { type: "MemberRenewal", id: renewalId },
        { type: "Transitions", id: renewalId },
        "MemberRenewal",
      ],
    }),

    // Reset (terminate + clear) the Zoho contract without changing status
    resetRenewalContract: builder.mutation({
      query: ({ renewalId }) => ({
        url: `${renewalId}/reset-contract`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { renewalId }) => [
        { type: "MemberRenewal", id: renewalId },
        "MemberRenewal",
      ],
    }),
  }),
});

export const {
  useGetMemberRenewalsQuery,
  useGetAllMemberRenewalsQuery,
  useLazyGetAllMemberRenewalsQuery,
  useGetMemberRenewalByIdQuery,
  useCreateMemberRenewalMutation,
  useUpdateMemberRenewalMutation,
  useDeleteMemberRenewalMutation,
  useTriggerStatusTransitionMutation,
  useRevertMemberRenewalStatusMutation,
  useSetManualRenewalStatusMutation,
  useResetRenewalContractMutation,
} = memberRenewalApi;
